import { useState, useEffect } from "react";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import { apiUrl, clearTokens } from "../../utils/jwt-storage";
import { spaNavigate } from "../../utils/navigation";
import { refreshAuthHeaders } from "../../providers/ApiProvider";

/**
 * React port of ``users/templates/users/new-ui/verify_email_required.html``.
 *
 * Landing after signup when the account was created but the email
 * verification link hasn't been clicked yet. Shows a "check your inbox"
 * message + a Resend button (server-side exponential backoff via
 * ``/verify-email/resend/``) + a "Sign out and use a different account"
 * escape hatch that clears the local JWT so the user can go back to
 * ``/auth/login`` and try a different email without being trapped in
 * the unverified state.
 *
 * On mobile (Capacitor bundled SPA) this is the FIRST screen after a
 * signup submit — the boot script never navigates away from the local
 * bundle post-pivot, so all the "check your inbox" logic lives here.
 */
// Copy for each error slug the Django verify-email view might redirect
// with (``?e=expired``, ``?e=invalid``, ``?e=offline``). Fallback for
// any unknown slug is undefined → no error banner rendered.
//
// ``invalid`` is intentionally soft — the token might be genuinely
// invalid, OR already used, OR the account may have been removed
// between signup and click. We can't distinguish server-side (the
// cascade delete removed the token row too), so the copy covers all
// three without lying about which one.
const ERROR_COPY: Record<string, string> = {
  expired: "That verification link has expired. Click Resend below to get a fresh one.",
  invalid:
    "This link is no longer valid. It may have expired, been used already, or the account may have been removed. Sign up again or contact support if this was a mistake.",
  offline: "You look offline, email verification runs against our cloud. Reconnect and try again.",
};

export const VerifyEmailRequired = () => {
  // Seed the status from ``?e=…`` (Django's verify_email_view redirects
  // failed link clicks here with an error slug). If nothing's there,
  // start with the generic waiting-for-verify state.
  const initialErr = (() => {
    try {
      const slug = new URLSearchParams(window.location.search).get("e") || "";
      return ERROR_COPY[slug] ? { text: ERROR_COPY[slug], kind: "error" as const } : null;
    } catch {
      return null;
    }
  })();
  const [status, setStatus] = useState<string>(initialErr?.text || "");
  const [statusKind, setStatusKind] = useState<"info" | "success" | "error" | "offline">(initialErr?.kind || "info");
  const [cooldown, setCooldown] = useState(0);
  const [email, setEmail] = useState<string>("your inbox");
  const disabled = cooldown > 0;

  // Cross-device verify auto-detect. Poll whoami every 6s. When
  // ``is_email_verified`` flips true (the user clicked the link in
  // a different browser / on their phone / on desktop while this
  // screen sits on mobile), navigate home automatically so they
  // don't have to reload or hunt for a button. The first tick also
  // populates the email header ("we sent a link to X") so the copy
  // isn't generic.
  //
  // Backoff: 6s while cold, doubles to 30s after 2 minutes of no
  // change so a phone left on the wait page doesn't hammer the
  // cloud. Halts entirely on offline.
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let interval = 6000;
    const start = Date.now();

    const tick = () => {
      if (cancelled) return;
      // Capture the token at REQUEST time — not at response time.
      // Signup can finish setting the JWT between the first tick
      // firing (no token) and its 401 landing (token now present),
      // which used to falsely trip the "account revoked" branch on
      // a perfectly normal fresh signup. Reading the token once and
      // remembering whether we actually sent it removes the race.
      const tokenAtSend = window.localStorage?.getItem("afri:jwt:access") || null;
      const opts: RequestInit = tokenAtSend ? { headers: { Authorization: `Bearer ${tokenAtSend}` } } : {};
      console.info("[VerifyEmailRequired] poll tick", {
        hasToken: !!tokenAtSend,
        tokenPrefix: tokenAtSend?.slice(0, 20),
      });
      fetch(apiUrl("/api/current-user/whoami"), opts)
        .then((r) => {
          // We DELIBERATELY do not treat a 401 here as "account
          // revoked, boot to signup". The wait screen is often the
          // FIRST place a fresh JWT lands, and the very first poll
          // can transiently 401 for a bunch of reasons (JWT header
          // not yet propagated to the wrapped fetch on Capacitor,
          // stale IDB cache serving an old 401 shape, brand-new
          // JWT signature verification catching a clock skew, etc).
          // Booting the user to signup on the first 401 was a
          // frequent false-positive — matched exactly one real
          // scenario (admin deleted account while user sat on the
          // wait screen) and mis-fired on every clean fresh signup.
          // If the account IS truly gone, the user still sees "no
          // verification arrived" and can sign out via the button
          // below — a much better outcome than being yanked away
          // from a valid wait screen. Ignore 401 here; only 200 with
          // is_email_verified=true triggers the auto-navigate.
          return r.ok ? r.json() : null;
        })
        .then((body) => {
          if (cancelled) return;
          if (body && typeof body.email === "string") setEmail(body.email);
          if (body && body.is_email_verified === true) {
            // Verified elsewhere — send them home immediately.
            spaNavigate("/", true);
            return;
          }
          // Grow the interval after 2 min so we're not busy-polling.
          if (Date.now() - start > 120_000) interval = 30_000;
          timer = window.setTimeout(tick, interval);
        })
        .catch(() => {
          // Offline / cloud unreachable — keep the timer alive but
          // slower so we resume when the network is back.
          timer = window.setTimeout(tick, 15_000);
        });
    };

    // First tick right away so the email header populates.
    tick();
    // Refire when the app comes back to foreground. iOS Simulator's
    // setTimeout can drift or halt while the sim isn't the active
    // window; on real iOS the WebView pauses timers when
    // backgrounded. Either way, when the user returns to the app
    // (they just verified in another tab / on another device), we
    // want to check immediately, not wait for the next scheduled
    // tick.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        console.info("[VerifyEmailRequired] visibility → visible, force tick");
        if (timer !== null) window.clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Countdown ticker for the Resend cooldown. Fires only when
  // ``cooldown > 0`` and stops itself when the counter hits zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const onResend = async () => {
    if (disabled) return;
    setStatus("Sending…");
    setStatusKind("info");
    try {
      const res = await fetch(apiUrl("/verify-email/resend/"), {
        method: "POST",
        credentials: "include", // Django session cookie may auth this
        headers: { Accept: "application/json" },
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (res.status === 202) {
        setStatus("Sent. Check your inbox in a moment.");
        setStatusKind("success");
        setCooldown(60);
      } else if (res.status === 200) {
        // Server says the user is already verified. Route to login so
        // they can sign in with the verified account cleanly.
        setStatus("Already verified, taking you to sign in.");
        setStatusKind("success");
        window.setTimeout(() => spaNavigate("/auth/login", true), 800);
      } else if (res.status === 429) {
        const wait = (body.retry_after as number) || 60;
        setStatus(`Too many resends. Try again in ${wait}s.`);
        setStatusKind("error");
        setCooldown(wait);
      } else if (res.status === 503 && body.code === "offline") {
        setStatus((body.error as string) || "You look offline, reconnect and try again.");
        setStatusKind("offline");
      } else {
        setStatus((body.error as string) || `Send failed (HTTP ${res.status}). Try again in a moment.`);
        setStatusKind("error");
      }
    } catch {
      setStatus("You look offline, reconnect and try again.");
      setStatusKind("offline");
    }
  };

  const onSignOut = () => {
    // Clear the JWT so the boot-script's next JWT check sees no active
    // session and routes to /auth/login.
    try {
      clearTokens();
      refreshAuthHeaders();
    } catch {
      /* private browsing */
    }
    spaNavigate("/auth/login", true);
  };

  const statusColor =
    statusKind === "success"
      ? "var(--color-positive-content, #047857)"
      : statusKind === "offline"
        ? "var(--color-warning-content, #92571c)"
        : statusKind === "error"
          ? "var(--color-negative-content, #b91c1c)"
          : "var(--color-neutral-content-subtler, #4b5563)";

  const onManualCheck = async () => {
    setStatus("Checking…");
    setStatusKind("info");
    try {
      const t = window.localStorage?.getItem("afri:jwt:access");
      const r = await fetch(apiUrl("/api/current-user/whoami"), t ? { headers: { Authorization: `Bearer ${t}` } } : {});
      // 401 here is NOT treated as revoked — same reason the poller
      // ignores it. Transient 401 must not boot the user to signup.
      const body = r.ok ? await r.json() : null;
      if (body && body.is_email_verified === true) {
        spaNavigate("/", true);
        return;
      }
      setStatus("Not verified yet, click the link in your inbox first.");
      setStatusKind("info");
    } catch {
      setStatus("You look offline, reconnect and try again.");
      setStatusKind("offline");
    }
  };

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Check your inbox</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <p style={{ margin: "0 0 16px" }}>
          Verification link sent to <strong>{email}</strong>. This page refreshes automatically once you verify.
        </p>
        <button
          type="button"
          aria-label="Resend verification email"
          className="login-button"
          disabled={disabled}
          onClick={onResend}
          style={{ marginBottom: 8 }}
        >
          {disabled ? `Resend in ${cooldown}s` : "Resend email"}
        </button>
        <button
          type="button"
          aria-label="Check verification status now"
          className="login-button"
          onClick={onManualCheck}
          style={{
            background: "transparent",
            color: "var(--color-neutral-content)",
            border: "1px solid var(--color-neutral-border)",
          }}
        >
          I've verified — check now
        </button>
        {status && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "var(--font-size-body-small)",
              color: statusColor,
            }}
          >
            {status}
          </p>
        )}
        <div className="text-wrapper" style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--color-primary-content, #1e40af)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
              fontSize: "var(--font-size-body-small)",
            }}
          >
            Use a different email
          </button>
        </div>
      </div>
    </AuthShell>
  );
};

export default VerifyEmailRequired;
